import {
  PanelBody,
  ToggleControl,
  __experimentalNumberControl as NumberControl,
  __experimentalToggleGroupControl as ToggleGroupControl,
  __experimentalToggleGroupControlOption as ToggleGroupControlOption,
  __experimentalUnitControl as UnitControl,
} from "@wordpress/components";
import { __, sprintf } from "@wordpress/i18n";

import SliderControlsPanel from "../../utils/slider-controls-panel";

const DEFAULT_AXIS = { desktop: "x", tablet: "x", mobile: "x" };

/**
 * Breakpoint rows for the scroll-direction control.
 *
 * Ranges match the media queries in embla.min.css, which are also the ones
 * embla-extra.js hands Embla — the label states them so an author is not left
 * guessing where a rail flips from vertical back to horizontal.
 */
const AXIS_BREAKPOINTS = [
  { key: "desktop", label: __("Desktop", "brandy-blocks") },
  { key: "tablet", label: __("Tablet (480–1023px)", "brandy-blocks") },
  { key: "mobile", label: __("Mobile (≤479px)", "brandy-blocks") },
];

/**
 * Slider settings for the loop parent blocks (brandy/product-collection,
 * brandy/posts).
 *
 * They live nested in the block's `slider` attribute rather than at the top
 * level, so the shared controls picker — which reads
 * attributes.controls{Placement,Nav,Dots,Reveal} — is handed the slider object as
 * its `attributes` and the nested updater as its `setAttributes`. Same shape it
 * expects, one object deeper. This mirrors how PHP resolves the same keys:
 * SliderControls::styles() is called with the slider settings as `$attrs`.
 *
 * @param {string} templateLabel Name of the child template block, used where the
 *                               help text points at the block that owns the gap.
 */
export default function LoopSliderPanel({
  slider = {},
  updateSlider,
  templateLabel = __("Post Template", "brandy-blocks"),
  itemLabel = __("featured image", "brandy-blocks"),
}) {
  const axis = { ...DEFAULT_AXIS, ...(slider.axis || {}) };
  const hasVerticalAxis = Object.values(axis).includes("y");

  return (
    <PanelBody title={__("Slider", "brandy-blocks")} initialOpen={false}>
      <ToggleControl
        __nextHasNoMarginBottom
        label={__("Display as slider", "brandy-blocks")}
        help={sprintf(
          /* translators: %s: name of the child template block. */
          __(
            "Uses the column counts from Layout as slides per view, and the %s's block spacing as the gap. The carousel runs on the frontend.",
            "brandy-blocks"
          ),
          templateLabel
        )}
        checked={!!slider.enabled}
        onChange={(value) => updateSlider({ enabled: value })}
      />
      {slider.enabled && (
        <>
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Loop", "brandy-blocks")}
            checked={slider.loop ?? true}
            onChange={(value) => updateSlider({ loop: value })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Navigation arrows", "brandy-blocks")}
            checked={slider.showButtons ?? true}
            onChange={(value) => updateSlider({ showButtons: value })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Pagination dots", "brandy-blocks")}
            checked={!!slider.showDots}
            onChange={(value) => updateSlider({ showDots: value })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Centre arrows on the image", "brandy-blocks")}
            help={sprintf(
              /* translators: %s: what the arrows are centred on, e.g. "featured image". */
              __(
                "Pins the arrows to the middle of the %s instead of the whole card, so a row of uneven cards keeps its arrows level.",
                "brandy-blocks"
              ),
              itemLabel
            )}
            checked={!!slider.buttonsOffsetBasedOnImage}
            onChange={(value) =>
              updateSlider({ buttonsOffsetBasedOnImage: value })
            }
          />
          {AXIS_BREAKPOINTS.map(({ key, label }) => (
            <ToggleGroupControl
              key={key}
              __next40pxDefaultSize
              __nextHasNoMarginBottom
              isBlock
              label={sprintf(
                /* translators: %s: breakpoint name, e.g. "Desktop". */
                __("Scroll direction — %s", "brandy-blocks"),
                label
              )}
              value={axis[key]}
              onChange={(value) =>
                updateSlider({ axis: { ...axis, [key]: value } })
              }
            >
              <ToggleGroupControlOption
                value="x"
                label={__("Horizontal", "brandy-blocks")}
              />
              <ToggleGroupControlOption
                value="y"
                label={__("Vertical", "brandy-blocks")}
              />
            </ToggleGroupControl>
          ))}
          {hasVerticalAxis && (
            <UnitControl
              __next40pxDefaultSize
              label={__("Track height", "brandy-blocks")}
              help={__(
                "How tall the vertical rail is. A horizontal track takes its height from the tallest card, but a vertical one has to be told where to stop or every card shows at once with nothing to scroll.",
                "brandy-blocks"
              )}
              value={slider.viewportHeight || "600px"}
              units={[
                { value: "px", label: "px" },
                { value: "vh", label: "vh" },
                { value: "rem", label: "rem" },
              ]}
              onChange={(value) => updateSlider({ viewportHeight: value })}
            />
          )}
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Fade the edges", "brandy-blocks")}
            help={__(
              "Cards dissolve into the background where they leave the rail. Only the sides that still have cards to reach are faded, so a rail parked at its first card keeps a clean edge.",
              "brandy-blocks"
            )}
            checked={!!slider.fadeEdges}
            onChange={(value) => updateSlider({ fadeEdges: value })}
          />
          {slider.fadeEdges && (
            <UnitControl
              __next40pxDefaultSize
              label={__("Fade width", "brandy-blocks")}
              help={__("How far into the rail the fade reaches.", "brandy-blocks")}
              value={slider.fadeSize || "48px"}
              units={[
                { value: "px", label: "px" },
                { value: "rem", label: "rem" },
                { value: "%", label: "%" },
              ]}
              onChange={(value) => updateSlider({ fadeSize: value })}
            />
          )}
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Autoplay", "brandy-blocks")}
            checked={!!slider.autoPlay}
            onChange={(value) => updateSlider({ autoPlay: value })}
          />
          {slider.autoPlay && (
            <>
              <NumberControl
                __next40pxDefaultSize
                label={__("Autoplay delay (ms)", "brandy-blocks")}
                value={slider.autoPlayDelay ?? 3000}
                min={500}
                step={500}
                onChange={(value) =>
                  updateSlider({ autoPlayDelay: parseInt(value, 10) || 3000 })
                }
              />
              <ToggleControl
                __nextHasNoMarginBottom
                label={__("Pause on hover", "brandy-blocks")}
                help={__(
                  "Stops the rail while the pointer rests on it. Needed when the cards carry buttons or hover-revealed actions, which a moving rail carries out from under the pointer.",
                  "brandy-blocks"
                )}
                checked={!!slider.pauseOnHover}
                onChange={(value) => updateSlider({ pauseOnHover: value })}
              />
            </>
          )}
          <SliderControlsPanel
            attributes={slider}
            setAttributes={updateSlider}
          />
        </>
      )}
    </PanelBody>
  );
}
